import { FormEvent, FunctionComponent, useCallback, useId, useMemo, useState } from 'react';
import Button from '../../../Button';
import Alert from '../../../Alert';
import { Address, Invoice as InvoiceType } from '@shared/types';
import { useStatePart } from '../../../../hooks/useStatePart';
import styles from './styles.module.scss';
import { AddressInput, Input } from '@cademy-ui/form';
import { CheckSolidIcon } from '../../../Icons';
import { LocalDate } from '../../../LocalDate';
import { Temporal } from 'temporal-polyfill';

type InvoiceProps = {
    initialInvoiceDetails?: InvoiceType;
    onSubmit: (invoiceDetails: InvoiceType) => unknown;
};

export const Invoice: FunctionComponent<InvoiceProps> = ({ initialInvoiceDetails, onSubmit }) => {
    const id = useId();
    const [invoiceDetails, setInvoiceDetails] = useState<InvoiceType>(
        initialInvoiceDetails || {
            billing_address: null,
            billing_name: null,
            billing_email: null,
            invoice_ref: null,
            recipient_instructions: null,
            vat_number: null,
        }
    );

    const [billingName, setBillingName] = useStatePart(
        invoiceDetails,
        setInvoiceDetails,
        'billing_name'
    );
    const [billingEmail, setBillingEmail] = useStatePart(
        invoiceDetails,
        setInvoiceDetails,
        'billing_email'
    );
    const [vatNumber, setVatNumber] = useStatePart(invoiceDetails, setInvoiceDetails, 'vat_number');
    const [invoiceRef, setInvoiceRef] = useStatePart(
        invoiceDetails,
        setInvoiceDetails,
        'invoice_ref'
    );

    const [billingAddress, setBillingAddress] = useStatePart(
        invoiceDetails,
        setInvoiceDetails,
        'billing_address'
    );

    const onFormSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            onSubmit(invoiceDetails);
        },
        [invoiceDetails, onSubmit]
    );

    const dueDate = useMemo(() => {
        if (!invoiceDetails.due_date) {
            return null;
        }
        return (
            <LocalDate
                date={Temporal.Instant.fromEpochSeconds(invoiceDetails.due_date)}
                format={{ dateStyle: 'long' }}
            />
        );
    }, [invoiceDetails.due_date]);

    return (
        <form onSubmit={onFormSubmit} className={styles.container}>
            <Input
                label="Business or Billing Name"
                id={`${id}_billingNameInput`}
                type="text"
                value={billingName || ''}
                name="billing_name"
                required={true}
                onChange={(val) => setBillingName(val)}
            />
            <Input
                label="Billing Email (optional)"
                id={`${id}_billingEmailInput`}
                type="email"
                value={billingEmail || ''}
                name="billing_email"
                onChange={(val) => setBillingEmail(val)}
            />
            <Input
                label="Your Tax / VAT number (optional)"
                id={`${id}_vatNumberInput`}
                type="text"
                value={vatNumber || ''}
                name="vat_number"
                onChange={(val) => setVatNumber(val)}
            />
            <Input
                label="Additional billing instructions / PO number (optional)"
                id={`${id}_invoiceRefInput`}
                type="text"
                value={invoiceRef || ''}
                name="invoice_ref"
                onChange={(val) => setInvoiceRef(val)}
            />

            <AddressInput
                label="Billing Address"
                id={`${id}_billingAddressInput`}
                name="billing_address"
                value={billingAddress}
                required={true}
                onChange={(newAddress: Address | null) => setBillingAddress(newAddress)}
            />

            {dueDate ? (
                <Alert preset="info" className={styles.alert}>
                    This invoice will be due by {dueDate}.
                </Alert>
            ) : null}

            <Button type="submit" preset="primary" className={styles.payBtn}>
                <CheckSolidIcon />
                Pay by Invoice
            </Button>
        </form>
    );
};
